import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import { Tooltip } from "react-tooltip";

import { PaymentsSubscriptionCreateAttributes } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import { Icon } from "~/assets/icons/Icon";
import RangeSlider from "~/components/ui/rangeSlider/RangeSlider";
import { useTeam } from "~/contexts/TeamProvider";
import { REQUEST_DEMO_URL } from "~/helpers/consts";

import { RangeValue } from "../../../api/types";
import { sortByDivideBy, withId } from "../../../helpers/helpers";
import Switch from "../../switch/Switch";
import Button from "../../ui/button/Button";
import { Addon, Feature, Plan } from "../types";
import {
  features,
  featuresPlus,
  getAddonPrice,
  getPrice,
  getTotalPlanPrice,
} from "../utils";
import styles from "./PricingCard.module.scss";

interface PricingCardProps {
  plan: Plan;
  mutate: (data: Omit<PaymentsSubscriptionCreateAttributes, "ua">) => void;
  subsctiptionPriceId: number | undefined;
  /** Personalized-link flow: the link bypasses the demo requirement. */
  personalized?: boolean;
}

// A list of globally known metrics, "extendable" by add-ons.
const knownMetrics = ["page_max", "keyword_max", "site_max", "membership_max"];

export const PricingCard: React.FC<PricingCardProps> = ({
  plan,
  mutate,
  subsctiptionPriceId,
  personalized,
}) => {
  const { team } = useTeam();
  const hasSubscription = !!team?.subscription;
  const { data: planUsage } = useQuery({
    queryKey: ["planUsage"],
    queryFn: async () => {
      const response = await apiClient.teams.usageList();

      return response?.data?.team_usage;
    },
  });

  // Select metrics where we have an extension via an add-on
  const availableMetrics = Object.fromEntries(
    knownMetrics
      .map((metric: string): [string, Addon | undefined] => {
        return [
          metric,
          plan.addons.optionalAddons?.find((a: Addon) => a.key === metric),
        ];
      })
      .filter(([, addon]) => !!addon),
  );

  const [rangeValue, setRangeValue] = useState<RangeValue>(() => {
    const initial: RangeValue = {
      page_max: {
        quantity: availableMetrics["page_max"]?.quantity || 0,
        priceId: availableMetrics["page_max"]?.priceId || 0,
      },
      keyword_max: {
        quantity: availableMetrics["keyword_max"]?.quantity || 0,
        priceId: availableMetrics["keyword_max"]?.priceId || 0,
      },
      site_max: {
        quantity: availableMetrics["site_max"]?.quantity || 0,
        priceId: availableMetrics["site_max"]?.priceId || 0,
      },
      membership_max: {
        quantity: availableMetrics["membership_max"]?.quantity || 0,
        priceId: availableMetrics["membership_max"]?.priceId || 0,
      },
    };

    // Seed singleton (toggle) addons so their priceId is captured for
    // submission and their initial on/off state reflects the existing
    // subscription. Non-singleton addons outside `knownMetrics` are not
    // seeded — that is a pre-existing limitation of this card.
    (plan.addons.optionalAddons ?? []).forEach((addon) => {
      if (addon.singleton && addon.key && !initial[addon.key]) {
        initial[addon.key] = {
          quantity: addon.quantity ?? 0,
          priceId: addon.priceId ?? 0,
        };
      }
    });

    return initial;
  });

  // Add-on quantities as they stood on mount. The current plan's Update
  // button stays disabled until something differs from this baseline — a
  // plan with no adjustable add-ons has nothing to update.
  const baselineRangeValue = useRef(rangeValue);
  const hasChanges = Object.keys(rangeValue).some(
    (key) =>
      rangeValue[key].quantity !== baselineRangeValue.current[key]?.quantity,
  );

  const [planPrice, setPlanPrice] = useState(
    getTotalPlanPrice(plan, rangeValue),
  );

  useEffect(() => {
    setPlanPrice(getTotalPlanPrice(plan, rangeValue));
  }, [plan, rangeValue]);

  const handleChange = (value: number, key: string | number) => {
    setRangeValue((state) => ({
      ...state,
      [key]: { ...state?.[key], quantity: value },
    }));
  };

  const handleSubmit = () => {
    mutate({
      price_id: plan.priceId,
      subscription_addons_attributes: Object.keys(rangeValue)
        .filter((metric) => rangeValue[metric].priceId > 0)
        .map((metric) => {
          return {
            quantity: rangeValue[metric].quantity,
            price_id: rangeValue[metric].priceId,
            ...withId(availableMetrics[metric]?.subscriptionProdunctId),
          };
        }),
    });
  };

  const isCurrentPlan = plan.priceId === subsctiptionPriceId;
  // Mirrors the backend demo_required validation: teams on a paid
  // subscription (active or past_due) may switch to demo plans freely,
  // and a personalized link bypasses the requirement entirely.
  const isPaidSubscription =
    team?.subscription?.status === "active" ||
    team?.subscription?.status === "past_due";
  const requiresDemo =
    Boolean(plan?.demo) && !personalized && !isPaidSubscription;

  return (
    <>
      <div key={plan.id} className={styles.plan}>
        <h2 className={styles.planTitle}>{plan.name}</h2>
        {plan?.highlight && <p className={styles.planHighlight}>Popular</p>}
        {isCurrentPlan && <p className={styles.planHighlight}>Current</p>}
        <p className={styles.planDescr}>{plan.description}</p>
        <p className={styles.planPrice}>
          <span className={styles.planAmount}>
            {getPrice(Number(planPrice), plan?.currency)}
          </span>
          <span className={styles.planInterval}> / {plan.interval}</span>
        </p>
        <ul className={styles.planListfeature}>
          {plan?.features.map((planFeature: Feature, index) => {
            return (
              <li key={index}>
                <Icon iconName="GreenCheckmarkIcon" />
                {/* The backend serializes unlimited caps as null */}
                {planFeature?.value ?? "Unlimited"}{" "}
                {features?.[planFeature.key as keyof typeof features]}
                {!!planFeature?.infoIconText &&
                  !!availableMetrics[planFeature.key] && (
                    <>
                      <a data-tooltip-id={planFeature.key}>
                        <Icon iconName="InfoIcon" width="1rem" height="1rem" />
                      </a>
                      <Tooltip
                        style={{ borderRadius: "var(--border-radius)" }}
                        id={planFeature.key}
                        content={planFeature.infoIconText}
                        place="top"
                      />
                    </>
                  )}
              </li>
            );
          })}
        </ul>
        <div className={styles.planDivider} />
        <ul className={styles.planListfeature}>
          {plan?.addons?.defaultAddons.map((addon) => {
            const tier = addon?.price?.tiers;
            const count = tier ? tier?.[0]?.up_to : "";

            const price =
              (tier
                ? Number(tier?.[1]?.unit_amount_decimal)
                : addon?.price?.amount) / 100;
            const addonPrice = getAddonPrice(price, plan?.currency);

            return (
              <li key={addon.id}>
                <Icon iconName="GreenCheckmarkIcon" />
                <span>
                  {` ${count} ${addon.name} ${tier ? "included, then" : "for"} ${addonPrice} per ${addon.label}`}
                </span>
              </li>
            );
          })}
        </ul>
        <div className={styles.planDivider} />
        <ul className={styles.planListfeature}>
          {plan?.featuresPlus.map((plan: Feature) => {
            return (
              <li key={plan.key}>
                {plan.value ? (
                  <Icon iconName="GreenCheckmarkIcon" />
                ) : (
                  <Icon iconName="RedCrossmarkIcon" />
                )}
                {featuresPlus[plan.key as keyof typeof featuresPlus]}{" "}
                {plan.value}
              </li>
            );
          })}
        </ul>
        <div className={styles.planDivider} />
        <ul className={styles.planListfeature}>
          {sortByDivideBy(plan?.addons?.optionalAddons)?.map((addon) => {
            const key = addon?.key || "";

            if (addon.singleton) {
              const isOn = (rangeValue?.[key]?.quantity ?? 0) > 0;
              const priceText = getPrice(
                addon.pureProductPrice || 0,
                plan?.currency || "usd",
              );

              return (
                <div key={key} className={styles.planToggle}>
                  <p>
                    {addon.name} for {priceText}
                  </p>
                  <Switch
                    checked={isOn}
                    onChange={(e) => {
                      const checked =
                        typeof e === "boolean"
                          ? e
                          : (e?.target?.checked ?? !isOn);
                      handleChange(checked ? 1 : 0, key);
                    }}
                  />
                </div>
              );
            }

            const addonQuantity = addon?.quantity ?? 0;
            const diff =
              addonQuantity +
              (planUsage?.plan_limits?.[
                addon.key as keyof typeof planUsage.plan_limits
              ] ?? 0) -
              (planUsage?.current_usage?.[
                addon.key as keyof typeof planUsage.current_usage
              ] ?? 0);
            const minValue = diff > 0 ? addonQuantity - diff : addonQuantity;

            return (
              <RangeSlider
                key={key}
                value={rangeValue?.[key]?.quantity}
                min={(minValue < 0 ? 0 : minValue) || 0}
                defaultValue={rangeValue?.[key]?.quantity}
                max={(addon?.divide_by ?? 1) * 100}
                step={addon?.divide_by}
                label={addon?.sliderRangeTitle}
                onChange={(e) => handleChange(e, key)}
                className={styles.planSlider}
              />
            );
          })}
        </ul>
        <div className={styles.planButtons}>
          {isCurrentPlan ? (
            <Button
              onClick={() => handleSubmit()}
              fullWidth
              disabled={!hasChanges}
            >
              Update
            </Button>
          ) : requiresDemo ? (
            <Button
              onClick={() => window.open(REQUEST_DEMO_URL, "_blank")}
              fullWidth
            >
              Request Demo
            </Button>
          ) : (
            <Button onClick={() => handleSubmit()} fullWidth>
              {hasSubscription ? "Switch to Plan" : "Get Started"}
            </Button>
          )}
        </div>
      </div>
    </>
  );
};
